import { useState } from "react"
import Layout from "@/components/Layout"
import DepreciationScheduleTable, { ScheduleRow } from "@/components/DepreciationScheduleTable"
import { exportToCSV } from "@/lib/exportCsv"
import {Laptop} from 'lucide-react'

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
}

const HARDWARE_TYPES = [
  { label: "Laptop / Desktop Computer (5-yr)", life: 5, method: "ddb" },
  { label: "Server / Network Equipment (5-yr)", life: 5, method: "ddb" },
  { label: "Peripherals / Printers (5-yr)", life: 5, method: "ddb" },
  { label: "Software / SaaS Infrastructure (3-yr)", life: 3, method: "ddb" },
  { label: "Telecommunications Equipment (7-yr)", life: 7, method: "ddb" },
]

export default function ComputerHardwareCalculator() {
  const [cost, setCost] = useState("")
  const [salvage, setSalvage] = useState("0")
  const [hwType, setHwType] = useState(0)
  const [schedule, setSchedule] = useState<ScheduleRow[]>([])
  const [results, setResults] = useState<{ yr1: number; totalDepr: number; method: string } | null>(null)
  const [error, setError] = useState("")

  function calculate() {
    setError("")
    const c = parseFloat(cost)
    const s = parseFloat(salvage)
    const { life } = HARDWARE_TYPES[hwType]
    if (isNaN(c) || c <= 0) { setError("Please enter a valid asset cost."); return }
    if (isNaN(s) || s < 0) { setError("Please enter a valid salvage value."); return }
    if (s >= c) { setError("Salvage value must be less than cost."); return }

    const ddbRate = 2 / life
    const rows: ScheduleRow[] = []
    let bookValue = c
    let accumulated = 0

    for (let i = 1; i <= life; i++) {
      const begin = bookValue
      let depr = bookValue * ddbRate
      const slDepr = (bookValue - s) / (life - i + 1)
      if (slDepr > depr) depr = slDepr
      if (bookValue - depr < s) depr = bookValue - s
      if (depr < 0) depr = 0
      accumulated += depr
      bookValue -= depr
      rows.push({ year: i, beginningValue: begin, depreciation: depr, accumulatedDepreciation: accumulated, endingBookValue: bookValue })
      if (bookValue <= s) break
    }

    setSchedule(rows)
    setResults({ yr1: rows[0]?.depreciation ?? 0, totalDepr: c - s, method: "200% DDB → Straight Line" })
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-violet-600 rounded-lg p-2">
              <Laptop className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-violet-600 uppercase tracking-wide">Asset Specific</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Computer Depreciation & Hardware Life Calculator
          </h1>
          <p className="text-lg text-gray-600">
            Calculate depreciation for computers, servers, software, and IT equipment using
            IRS MACRS 5-year property class with double declining balance method.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Enter IT Asset Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hardware Type</label>
              <select
                value={hwType}
                onChange={(e) => setHwType(parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500 text-gray-900"
              >
                {HARDWARE_TYPES.map((h, i) => <option key={h.label} value={i}>{h.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Cost ($)</label>
              <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 2500"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Salvage Value ($)</label>
              <input type="number" value={salvage} onChange={(e) => setSalvage(e.target.value)} placeholder="e.g. 0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
          </div>
          {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}
          <button onClick={calculate}
            className="bg-violet-600 hover:bg-violet-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors">
            Calculate Hardware Depreciation
          </button>

          {results !== null ? (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-violet-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-violet-700">{fmt(results.yr1)}</div>
                <div className="text-sm text-gray-600 mt-1">Year 1 Deduction</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-700">{fmt(results.totalDepr)}</div>
                <div className="text-sm text-gray-600 mt-1">Total Depreciable</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 text-center">
                <div className="text-sm font-bold text-gray-700 pt-2">{results.method}</div>
                <div className="text-sm text-gray-600 mt-1">Depreciation Method</div>
              </div>
            </div>
          ) : null}
          {schedule.length > 0 ? <DepreciationScheduleTable rows={schedule} onExport={() => exportToCSV(schedule, "computer-depreciation-schedule.csv")} /> : null}
        </div>

        <div className="prose max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Computer Depreciation — IRS Rules</h2>
          <p className="text-gray-700 mb-4">
            Under IRS MACRS, computers and peripheral equipment are classified as <strong>5-year
            property</strong>. Businesses use the <strong>200% double declining balance method</strong>
            with a half-year convention, switching to straight line when advantageous.
          </p>
          <div className="overflow-x-auto rounded-xl border border-gray-200 mb-6">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Hardware Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">IRS Class</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Life</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Method</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Laptops, Desktops, Monitors", "5-Year", "5 yrs", "200% DDB"],
                  ["Servers, Network Switches", "5-Year", "5 yrs", "200% DDB"],
                  ["Software (purchased)", "3-Year", "3 yrs", "200% DDB"],
                  ["Telecom Equipment", "7-Year", "7 yrs", "200% DDB"],
                  ["Printers, Scanners", "5-Year", "5 yrs", "200% DDB"],
                ].map(([type, cls, life, method], i) => (
                  <tr key={type} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-4 py-3 text-gray-700">{type}</td>
                    <td className="px-4 py-3 text-violet-700 font-medium">{cls}</td>
                    <td className="px-4 py-3 text-gray-600">{life}</td>
                    <td className="px-4 py-3 text-gray-600">{method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <h3 className="font-semibold text-amber-900 mb-1">Section 179 Tip for IT Equipment</h3>
            <p className="text-amber-800 text-sm">
              Most IT hardware qualifies for <strong>Section 179</strong> expensing — you can
              deduct the full cost in Year 1 (up to $1,160,000 in 2023) rather than depreciating
              over 5 years. Ideal for businesses wanting maximum immediate tax deductions.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  )
}
